const BASE_URL = import.meta.env.BASE_URL || '/';
const NORMALIZED_BASE = BASE_URL.endsWith('/') ? BASE_URL : `${BASE_URL}/`;

export function withBasePath(value) {
  if (typeof value !== 'string' || value === '') return value;
  if (
    value.startsWith('http://') ||
    value.startsWith('https://') ||
    value.startsWith('//') ||
    value.startsWith('data:') ||
    value.startsWith('blob:') ||
    value.startsWith('#')
  ) {
    return value;
  }

  // Strip accidental /backoffice prefix from asset paths.
  let path = value.replace(/^\/backoffice(?=\/(?:images|assets|uploads)\/)/i, '');

  if (!path.startsWith('/')) {
    // Relative asset path → site-root absolute
    if (/^(images|assets|uploads)\//i.test(path)) path = `/${path}`;
    else return value;
  }

  // Already prefixed (prevents /stage/stage/...)
  if (NORMALIZED_BASE !== '/' && path.startsWith(NORMALIZED_BASE)) {
    return path;
  }

  return `${NORMALIZED_BASE}${path.replace(/^\/+/, '')}`;
}

/**
 * Normalize image/asset URLs in HTML so they never resolve under /backoffice/.
 * Converts TinyMCE relative paths and accidental /backoffice/images/... to root paths.
 */
export function rewriteAssetUrlsInHtml(html) {
  if (typeof html !== 'string' || html === '') return html;

  return html
    .replace(/((?:src|href)=["'])([^"']+)(["'])/gi, (match, pre, url, post) => {
      const next = normalizeAssetUrl(url);
      return next === url ? match : `${pre}${next}${post}`;
    })
    .replace(/(url\(["']?)([^"')]+)(["']?\))/gi, (match, pre, url, post) => {
      const next = normalizeAssetUrl(url);
      return next === url ? match : `${pre}${next}${post}`;
    });
}

function normalizeAssetUrl(url) {
  if (typeof url !== 'string' || !url) return url;
  const trimmed = url.trim();
  if (
    trimmed.startsWith('http://') ||
    trimmed.startsWith('https://') ||
    trimmed.startsWith('//') ||
    trimmed.startsWith('data:') ||
    trimmed.startsWith('blob:') ||
    trimmed.startsWith('#') ||
    trimmed.startsWith('mailto:') ||
    trimmed.startsWith('tel:')
  ) {
    return trimmed;
  }

  let path = trimmed
    // /backoffice/images/... → /images/...
    .replace(/^\/backoffice(?=\/(?:images|assets|uploads)\/)/i, '')
    // ../images/... or ../../images/... → /images/...
    .replace(/^(?:\.\.\/)+(images|assets|uploads)\//i, '/$1/')
    // ./images/... → /images/...
    .replace(/^\.\/(?=(?:images|assets|uploads)\/)/i, '/');

  // images/... (no leading slash) → /images/...
  if (/^(images|assets|uploads)\//i.test(path)) path = `/${path}`;

  if (!path.startsWith('/')) return trimmed;

  if (
    NORMALIZED_BASE !== '/' &&
    !path.startsWith(NORMALIZED_BASE) &&
    /\/(images|assets|uploads)\//i.test(path)
  ) {
    return `${NORMALIZED_BASE}${path.replace(/^\/+/, '')}`;
  }
  return path;
}
